import type { DefectsComboBoxProps } from "./DefectsComboBox.type";
import type { Suggestion } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions/DefectSuggestion.type";

import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Form, Tooltip } from "antd";
import isEqual from "lodash/isEqual";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import ComboBox from "@/components/Elements/ComboBox/ComboBox";
import { DEFECT_FIELD_DEPENDENCIES } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/constants";
import DefectSuggestion from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions/DefectSuggestion";
import SelectedDefectNotice from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions/SelectedDefectNotice";
import { SeverityGridPreview } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/gridUtils";
import { useDefectsFromReferential } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/hooks/useDefectsFromReferential";
import { useDefectsFromSeverityGrid } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/hooks/useDefectsFromSeverityGrid";
import { useIsFormEnginePreviewMode } from "@/components/FormEngine/hooks/useIsFormEnginePreviewMode";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { convertItemWithSlugToOption } from "@/modules/select";

import { normalizeDescription } from "./DefectsComboBoxHelpers";

const { useFormInstance } = Form;

const DefectsComboBox = ({
  value,
  onChange,
  datasetsImage = [],
  datasetsImageLoading,
  ...rest
}: DefectsComboBoxProps) => {
  const isDefectSuggestionsActivated = useFeatureFlag("defectSuggestions");
  const form = useFormInstance();
  const { t } = useTranslation();
  const isPreviewMode = useIsFormEnginePreviewMode();

  const { isLoadingReferential, defects } = useDefectsFromReferential();
  const { isLoadingGrid, isGridDefinedForAsset, gridDefects } =
    useDefectsFromSeverityGrid();
  const isLoading =
    isLoadingReferential || isLoadingGrid || datasetsImageLoading;

  const [selectedDefect, setSelectedDefect] = useState<Suggestion | undefined>(
    undefined
  );

  const GAP = 5;

  const getDefects = () => {
    if (isPreviewMode) {
      return SeverityGridPreview.buildSampleDefects();
    }

    if (isLoading) {
      return [];
    }

    if (isGridDefinedForAsset) {
      return gridDefects;
    }

    return defects;
  };

  const visibleDefects = getDefects();
  const initialOptions = visibleDefects
    .map((defect) =>
      convertItemWithSlugToOption({
        name: defect.name ?? "",
        slug: defect.slug ?? "",
      })
    )
    .filter((defect) => !!defect);

  const description =
    visibleDefects.find(({ slug }) => slug === value)?.description ?? "";

  const hasDescription = description ?? false;

  // TODO: Instead of reusing this logic, find a way to resize Select with CSS or Flex
  const iconSize = 0 + (hasDescription ? 14 : 0) + (selectedDefect ? 14 : 0);
  const marginSize =
    0 + (hasDescription ? GAP : 0) + (selectedDefect ? GAP : 0);

  const onDefectUpdate = (value?: string) => {
    form.resetFields([...DEFECT_FIELD_DEPENDENCIES]);
    onChange(value);
  };

  const onSelectedDefect = (params?: Suggestion) => {
    if (isDefectSuggestionsActivated && !isEqual(params, selectedDefect)) {
      setSelectedDefect(params);
    }
  };

  return (
    <div className="defects-combobox">
      <Flex
        justify="space-between"
        align="center"
        gap={selectedDefect || hasDescription ? `${GAP}px` : undefined}
      >
        <div className="selected-defect-notice-wrapper">
          {selectedDefect ? (
            <SelectedDefectNotice
              suggested={selectedDefect.suggested}
              name={selectedDefect.name}
              confidence={selectedDefect.confidence}
            />
          ) : null}
        </div>
        <ComboBox
          initialOptions={
            // TODO: Remove this eslint-disable-next-line when ComboBox will be in TS
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            initialOptions as any
          }
          placeholder={t("form:staticField.defect.placeholder")}
          value={value}
          {...rest}
          onChange={onDefectUpdate}
          style={{ width: `calc(100% - ${iconSize + marginSize}px)` }}
        />
        <div>
          {hasDescription && (
            <Tooltip title={normalizeDescription(description)}>
              <InfoCircleOutlined data-testid="InfoCircleOutlined" />
            </Tooltip>
          )}
        </div>
      </Flex>
      {isDefectSuggestionsActivated ? (
        <DefectSuggestion
          value={value}
          onChange={onDefectUpdate}
          onSelectedDefect={onSelectedDefect}
          visibleDefects={initialOptions}
          datasetsImage={datasetsImage}
          datasetsImageLoading={datasetsImageLoading}
        />
      ) : null}
    </div>
  );
};

export default DefectsComboBox;
